import {
  formatCurrency,
  transformDateToLocalDateString,
} from "helpers";
import { InvoiceIncomingRow } from "services/api/finance/incoming-invoices/types";
import { INCOMING_SOURCE_LABELS } from "services/api/finance/incoming-invoices/consts";
import { Badge } from "ui/components/Badge";
import { BadgeProps } from "ui/components/Badge/types";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import Tooltip from "ui/components/Tooltip/Tooltip";

export const VEHICLE_INCOMING_INVOICE_SEARCH_COLUMNS = (
  t: (key: string) => string,
  handleApproveClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  handleRejectClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  handleMarkPaidClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  handleDeleteClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void
): Column[] => {

  const getIncomingStatusBadgeVariant = (statusId: string | null): BadgeProps["variant"] => {
    switch (statusId) {
      case "received":
        return "info";
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      case "paid":
        return "success";
      case "partially_paid":
        return "warning";
      default:
        return "info";
    }
  };

  return [
    {
      title: "Broj računa",
      field: "invoiceNumber",
      type: ColumnTypes.string,
    },
    {
      title: "Dobavljač",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceIncomingRow) => {
        return (
          <div className="text-sm font-medium truncate">
            {row.contactSupplier?.name ?? row.supplierName ?? "-"}
          </div>
        );
      },
    },

    {
      title: "Status",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceIncomingRow) => {
        if (!row.status) return null;
        return (
          <Badge
            text={row.status.name}
            variant={getIncomingStatusBadgeVariant(row.status.id)}
            showDot
          />
        );
      },
    },
    {
      title: "Izvor",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceIncomingRow) => {
        if (!row.incomingSourceId) return <div className="text-sm text-slate-400">-</div>;
        const sourceName = INCOMING_SOURCE_LABELS[row.incomingSourceId] ?? row.incomingSourceId;
        return <Badge text={sourceName} variant="info" />;
      },
    },
    {
      title: "Datum računa",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceIncomingRow) => {
        return (
          <div>
            {transformDateToLocalDateString(row.invoiceDate)}
          </div>
        );
      },
    },
    {
      title: "Dospijeće",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceIncomingRow) => {
        return (
          <div>
            {transformDateToLocalDateString(row.dueDate)}
          </div>
        );
      },
    },
    {
      title: "Iznos",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceIncomingRow) => (
        <Tooltip
          decorate={<span className="text-md font-medium">{formatCurrency.format(row.total ?? 0)}</span>}
          position="bottom-right"
          size="medium"
        >
          <div className="min-w-[240px]">
            <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Pregled iznosa</div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Cijena bez PDV-a</span>
              <span className="font-medium">{formatCurrency.format(row.totalWithoutTax ?? 0)}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">PDV</span>
              <span className="font-medium">{formatCurrency.format(row.totalTax ?? 0)}</span>
            </div>
            <div className="border-t border-slate-600 my-2"></div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-300">Ukupno s PDV-om</span>
              <span className="font-bold text-green-400">{formatCurrency.format(row.total ?? 0)}</span>
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceIncomingRow) => {
        return (
          <ButtonMenu
            endIcon="/media/icons/more-dots.svg"
            cssClassName="h-full"
            variant="text"
          >
            <ButtonMenuItem
              startIcon="/media/icons/check.svg"
              onClick={(e) => handleApproveClick(e, row.id)}
            >
              Odobri
            </ButtonMenuItem>
            <ButtonMenuItem
              startIcon="/media/icons/close.svg"
              onClick={(e) => handleRejectClick(e, row.id)}
            >
              Odbij
            </ButtonMenuItem>
            <ButtonMenuItem
              startIcon="/media/icons/check.svg"
              onClick={(e) => handleMarkPaidClick(e, row.id)}
            >
              Označi plaćenim
            </ButtonMenuItem>
            <ButtonMenuItem
              startIcon="/media/icons/trash.svg"
              variant="remove"
              onClick={(e) => handleDeleteClick(e, row.id)}
            >
              Obriši
            </ButtonMenuItem>
          </ButtonMenu>
        );
      },
    },
  ]};
